import { useState, useEffect, useMemo } from "react";
import api from '../api/axios'; 
import { PageContainer, PageTitleBadge, DataGridTable } from "../components";
import { FaBell, FaEye } from "react-icons/fa";
import { useTranslation } from "../context/LanguageContext";
import "../styles/common.css";
import "./Alertes.css";

export default function Alertes() {
  const { t } = useTranslation();
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAnimaux, setSelectedAnimaux] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchAlertes = async () => {
      try {
        const response = await api.get('/api/alertes/');
        setAlertes(response.data);
      } catch (err) {
        setError(t.errorLoadingAlerts);
      } finally {
        setLoading(false);
      }
    };
    fetchAlertes();
  }, []);


  const groupedAlertes = alertes.reduce((acc, alerte) => {
    const uniqueKey = `${alerte.type}-${alerte.prochaine_intervention}`;
    if (!acc[uniqueKey]) {
      acc[uniqueKey] = { 
        type: alerte.type, 
        prochaineDate: alerte.prochaine_intervention, 
        animaux: []
      };
    }
    acc[uniqueKey].animaux.push(alerte);
    return acc;
  }, {});

  // Préparer les données pour DataGrid
  const rows = useMemo(() => {
    return Object.entries(groupedAlertes).map(([key, data], index) => ({
      id: key,
      type: data.type,
      nombre_animaux: data.animaux.length,
      prochaine_intervention: data.prochaineDate,
      animaux_details: data.animaux.map(a => 
        `Animal ${a.animal} – Troupeau ${a.troupeau} – Produit ${a.produit}`
      ).join('; '),
      animaux: data.animaux,
    }));
  }, [groupedAlertes]);

  // Configuration des colonnes pour DataGrid
  const columns = useMemo(() => [
    {
      field: 'type',
      headerName: t.interventionType,
      width: 200,
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'nombre_animaux',
      headerName: t.numberOfAnimals,
      width: 150,
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <span className="nombre-badge">{params.value}</span>
      ),
    },
    {
      field: 'prochaine_intervention',
      headerName: t.nextIntervention,
      width: 180,
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'animaux_details',
      headerName: t.details,
      width: 200,
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <button
          onClick={() => {
            setSelectedAnimaux(params.row.animaux);
            setShowModal(true);
          }}
          className="view-animaux-button"
        >
          <FaEye />
          <span>{t.viewAnimals} ({params.row.nombre_animaux})</span>
        </button>
      ),
    },
  ], [t]);

  if (loading) {
    return (
      <PageContainer>
        <div className="loading-container">
          <p className="loading-text">{t.loadingAlerts}</p>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageTitleBadge title={t.error} icon={<FaBell />} />
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitleBadge title={t.alertsTitle} icon={<FaBell />} />

      {/* DataGrid */}
      {alertes.length === 0 ? (
        <div className="empty-message-container">
          <p className="empty-message">{t.noInterventionsScheduled}</p>
        </div>
      ) : (
        <DataGridTable
          rows={rows}
          columns={columns}
          loading={loading}
          storageKey="alertes-zoom-level"
          initialSort={{ field: 'prochaine_intervention', sort: 'asc' }}
          pageSize={25}
          containerClassName="alertes-grid-container"
        />
      )}

      {/* Modal pour afficher les détails des animaux */}
      {showModal && selectedAnimaux && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content animaux-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header modal-header-success">
              <FaBell className="modal-icon modal-icon-success" />
              <h3>{t.animalDetails}</h3>
              <button
                className="close-modal-button"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="animaux-list">
                {selectedAnimaux.map((a, idx) => (
                  <div key={idx} className="animal-detail-item">
                    <div className="animal-detail-row">
                      <span className="animal-detail-label">{t.animal} :</span>
                      <span className="animal-detail-value">{a.animal}</span>
                    </div>
                    <div className="animal-detail-row">
                      <span className="animal-detail-label">{t.herd} :</span>
                      <span className="animal-detail-value">{a.troupeau}</span>
                    </div>
                    <div className="animal-detail-row">
                      <span className="animal-detail-label">{t.product} :</span>
                      <span className="animal-detail-value">{a.produit}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="modern-button modern-button-secondary"
                onClick={() => setShowModal(false)}
              >
                {t.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
